<template>
  <!-- Notification Popup with Animation -->
  <transition name="backdrop" appear>
    <div v-if="show" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click="handleBackdropClick">
      <transition name="notification" appear>
        <div 
          class="bg-white rounded-lg p-6 border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] w-full max-w-md mx-4 notification-popup"
          @click.stop
        >
          <div class="text-center">
            <!-- Success Icon with Animation -->
            <div v-if="type === 'success'" class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4 success-icon">
              <svg class="h-8 w-8 text-green-600 checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            
            <!-- Error Icon with Animation -->
            <div v-else class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4 error-icon">
              <svg class="h-8 w-8 text-red-600 cross" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>

            <!-- Title -->
            <h3 :class="[
              'text-lg font-medium mb-2',
              type === 'success' ? 'text-green-900' : 'text-red-900'
            ]">
              {{ title || (type === 'success' ? 'Success!' : 'Error') }}
            </h3>

            <!-- Message -->
            <p :class="[
              'text-sm mb-6',
              type === 'success' ? 'text-green-700' : 'text-red-700'
            ]">
              {{ message }}
            </p>

            <!-- Close Button -->
            <button
              @click="close"
              :class="[
                'w-full px-4 py-2 rounded-md font-medium transition-colors',
                type === 'success' 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-red-600 hover:bg-red-700 text-white'
              ]"
            >
              OK
            </button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

interface Props {
  show: boolean
  type: 'success' | 'error'
  title?: string
  message: string
  autoDismiss?: boolean
  dismissDelay?: number
}

const props = withDefaults(defineProps<Props>(), {
  autoDismiss: true,
  dismissDelay: 3000
})

const emit = defineEmits<{
  close: []
}>()

let dismissTimer: ReturnType<typeof setTimeout> | null = null

watch(() => props.show, (newValue) => {
  if (newValue && props.autoDismiss) {
    // Clear any existing timer
    if (dismissTimer) {
      clearTimeout(dismissTimer)
    }
    
    // Set new timer
    dismissTimer = setTimeout(() => {
      close()
    }, props.dismissDelay)
  }
})

const close = () => {
  if (dismissTimer) {
    clearTimeout(dismissTimer)
    dismissTimer = null
  }
  emit('close')
}

const handleBackdropClick = () => {
  close()
}
</script>

<style scoped>
/* Backdrop fade transition (fast and simple) */
.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

/* Notification popup animations (only for the content box) */
.notification-enter-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.notification-leave-active {
  transition: all 0.3s ease-in;
}

.notification-enter-from {
  opacity: 0;
  transform: scale(0.7) translateY(-50px);
}

.notification-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.notification-popup {
  animation: popupBounce 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes popupBounce {
  0% {
    transform: scale(0.3) translateY(-100px);
    opacity: 0;
  }
  50% {
    transform: scale(1.05) translateY(-10px);
    opacity: 0.8;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* Success icon animation */
.success-icon {
  animation: successPulse 0.6s ease-out 0.2s both;
}

@keyframes successPulse {
  0% {
    transform: scale(0);
    background-color: rgb(220 252 231);
  }
  50% {
    transform: scale(1.2);
    background-color: rgb(187 247 208);
  }
  100% {
    transform: scale(1);
    background-color: rgb(220 252 231);
  }
}

.checkmark {
  stroke-dasharray: 20;
  stroke-dashoffset: 20;
  animation: checkmarkDraw 0.5s ease-out 0.4s both;
}

@keyframes checkmarkDraw {
  to {
    stroke-dashoffset: 0;
  }
}

/* Error icon animation */
.error-icon {
  animation: errorShake 0.5s ease-out 0.2s both;
}

@keyframes errorShake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-3px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(3px);
  }
}

.cross {
  stroke-dasharray: 20;
  stroke-dashoffset: 20;
  animation: crossDraw 0.3s ease-out 0.3s both;
}

@keyframes crossDraw {
  to {
    stroke-dashoffset: 0;
  }
}
</style>


